import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { JSNS, type AttachmentRef, type Conversation, type Folder, type Message } from '@zimbra-webif/sdk';
import { useClient } from './ClientContext';

const PAGE = 50;

/** One page of list results + whether the server has more. */
interface Page<T> {
  items: T[];
  more: boolean;
  offset: number;
}

/** Mailbox root folder tree (USER_ROOT and descendants, incl. shared links). */
export function useFolders() {
  const { client } = useClient();
  return useQuery({
    queryKey: ['folders'],
    queryFn: async (): Promise<Folder | undefined> => {
      const res = await client.mail.getFolderTree();
      return res.folder?.[0];
    },
  });
}

/** Messages in a folder (message view), paged for infinite scroll. */
export function useMessages(folderId: string, sortBy: string) {
  const { client } = useClient();
  return useInfiniteQuery({
    queryKey: ['messages', folderId, sortBy],
    initialPageParam: 0,
    queryFn: async ({ pageParam }): Promise<Page<Message>> => {
      const res = await client.mail.search({
        query: `inid:${folderId}`,
        types: 'message',
        sortBy,
        limit: PAGE,
        offset: pageParam,
      });
      const r = res as { m?: Message[]; more?: boolean };
      return { items: (r.m ?? []) as Message[], more: !!r.more, offset: pageParam };
    },
    getNextPageParam: (last) => (last.more ? last.offset + PAGE : undefined),
  });
}

/** Conversations (threads) in a folder, paged for infinite scroll. */
export function useConversations(folderId: string, sortBy: string) {
  const { client } = useClient();
  return useInfiniteQuery({
    queryKey: ['conversations', folderId, sortBy],
    initialPageParam: 0,
    queryFn: async ({ pageParam }): Promise<Page<Conversation>> => {
      const res = await client.mail.search({
        query: `inid:${folderId}`,
        types: 'conversation',
        sortBy,
        limit: PAGE,
        offset: pageParam,
      });
      const r = res as { c?: Conversation[]; more?: boolean };
      return { items: (r.c ?? []) as Conversation[], more: !!r.more, offset: pageParam };
    },
    getNextPageParam: (last) => (last.more ? last.offset + PAGE : undefined),
  });
}

/** All messages of a conversation, bodies expanded (the thread). */
export function useConversation(id: string | null) {
  const { client } = useClient();
  return useQuery({
    queryKey: ['conversation', id],
    enabled: id !== null,
    queryFn: async (): Promise<Message[]> => {
      const res = await client.mail.getConversation({
        id: id as string,
        fetch: 'all',
        html: true,
        headers: ['Message-ID', 'In-Reply-To', 'References'],
      });
      return (res.c?.[0]?.m ?? []) as Message[];
    },
  });
}

/** A single message with bodies expanded. */
export function useMessage(id: string | null) {
  const { client } = useClient();
  return useQuery({
    queryKey: ['message', id],
    enabled: id !== null,
    queryFn: async (): Promise<Message | undefined> => {
      // request the SpamAssassin score header for the spam meter.
      // neuter: 0 — Zimbra otherwise renames every remote <img src> to dfsrc (and
      // background to dfbackground), so no remote image ever showed. Remote-image
      // privacy is enforced in the reading iframe instead (CSP, "Load remote images").
      const res = await client.call<{ m?: Message[] }>(JSNS.mail, 'GetMsg', {
        m: { id, html: 1, neuter: 0, header: [{ n: 'X-Spam-Score' }, { n: 'X-Spam-Status' }] },
      });
      return res.m?.[0];
    },
  });
}

/** Full-text search across messages, paged for infinite scroll. */
/**
 * Full-text search. Always spans the whole mailbox; when shared folder ids are
 * given (mountpoints from other accounts) it ORs `inid:<id>` clauses so results
 * include the user's shares too.
 */
export function useSearch(query: string, sharedIds: string[] = []) {
  const { client } = useClient();
  const term = query.trim();
  const full =
    term && sharedIds.length > 0
      ? `(${term})${sharedIds.map((id) => ` OR (inid:${id} (${term}))`).join('')}`
      : term;
  return useInfiniteQuery({
    queryKey: ['search', full],
    enabled: term.length > 0,
    initialPageParam: 0,
    queryFn: async ({ pageParam }): Promise<Page<Message>> => {
      const res = await client.mail.search({
        query: full,
        types: 'message',
        sortBy: 'dateDesc',
        limit: PAGE,
        offset: pageParam,
      });
      const r = res as { m?: Message[]; more?: boolean };
      return { items: (r.m ?? []) as Message[], more: !!r.more, offset: pageParam };
    },
    getNextPageParam: (last) => (last.more ? last.offset + PAGE : undefined),
  });
}

interface SigContent {
  type?: string;
  _content?: string;
}
interface Sig {
  id?: string;
  name?: string;
  content?: SigContent[];
}
function escHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
/** The account's default signature as HTML (text/plain is converted), '' if none. */
export function useSignature() {
  const { client } = useClient();
  return useQuery({
    queryKey: ['signature'],
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<string> => {
      const [sigRes, prefRes] = await Promise.all([
        client.call<{ signature?: Sig[] }>(JSNS.account, 'GetSignatures', {}),
        client
          .call<{ pref?: Array<{ name?: string; _content?: string }> }>(JSNS.account, 'GetPrefs', {
            pref: [{ name: 'zimbraPrefDefaultSignatureId' }],
          })
          .catch(() => ({ pref: [] }) as { pref?: Array<{ name?: string; _content?: string }> }),
      ]);
      const sigs = sigRes.signature ?? [];
      if (sigs.length === 0) return '';
      const defId = prefRes.pref?.find((p) => p.name === 'zimbraPrefDefaultSignatureId')?._content;
      const sig = sigs.find((s) => s.id === defId) ?? sigs[0];
      if (!sig) return '';
      const html = sig.content?.find((c) => c.type === 'text/html')?._content;
      if (html) return html;
      const text = sig.content?.find((c) => c.type === 'text/plain')?._content ?? '';
      return escHtml(text).replace(/\n/g, '<br>');
    },
  });
}

export interface ActionVars {
  kind: 'msg' | 'conv';
  ids: string | string[];
  op: string;
  folderId?: string;
}

/** Apply an op to messages or conversations; refresh lists + counts. */
export function useMailAction() {
  const { client } = useClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: ActionVars) => {
      if (v.kind === 'conv') {
        const id = Array.isArray(v.ids) ? v.ids.join(',') : v.ids;
        const action: Record<string, unknown> = { id, op: v.op };
        if (v.folderId) action.l = v.folderId;
        return client.call(JSNS.mail, 'ConvAction', { action });
      }
      return client.mail.messageAction(v.ids, v.op, v.folderId ? { folderId: v.folderId } : {});
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['messages'] });
      void qc.invalidateQueries({ queryKey: ['conversations'] });
      void qc.invalidateQueries({ queryKey: ['folders'] });
    },
  });
}

export interface SendVars {
  to: Array<{ address: string; name?: string }>;
  cc?: Array<{ address: string; name?: string }>;
  bcc?: Array<{ address: string; name?: string }>;
  subject: string;
  text: string;
  html?: string;
  inReplyTo?: { origId: string; replyType: 'r' | 'w' };
  /** uploaded ids (from /api/upload) and/or parts of the original message being replied to / forwarded */
  attachments?: AttachmentRef[];
  /** the saved draft this compose continues: Save overwrites it, Send removes it */
  draftId?: string;
}

/** Save a composed message to Drafts; refresh Drafts + lists. */
export function useSaveDraft() {
  const { client } = useClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: SendVars) => client.compose.saveDraft(req),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['conversations'] });
      void qc.invalidateQueries({ queryKey: ['messages'] });
      void qc.invalidateQueries({ queryKey: ['folders'] });
    },
  });
}

/** Send a composed message; refresh Sent + lists. */
export function useSend() {
  const { client } = useClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: SendVars) => client.compose.sendMsg(req),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['conversations'] });
      void qc.invalidateQueries({ queryKey: ['messages'] });
      void qc.invalidateQueries({ queryKey: ['folders'] });
    },
  });
}
